import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { getUserInfo, MAX_MESSAGE_LENGTH } from '@autoplan/common'
import { Button, Card, Heading, Input, Stack, StatusMessage, TextArea } from '@autoplan/web-ui'
import { useLocale } from '@autoplan/web-sanity'
import { sendContactRequest, type ContactRequest } from '../api/api'

export const ContactForm = () => {
  const { t } = useLocale()
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const { register, handleSubmit, formState, reset, watch } = useForm<ContactRequest>({
    defaultValues: getDefaultValues(),
  })
  const messageLength = watch('message')?.length ?? 0

  const submit = handleSubmit(async (values) => {
    setStatus('idle')
    try {
      await sendContactRequest(values)
      reset({ ...values, message: '' })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  })

  return (
    <Card className="ap:p-2xl" background="white">
      <form className="ap:flex ap:flex-col ap:items-start ap:gap-2xl" onSubmit={submit}>
        <Heading size="lg">{t('home.contactForm.title')}</Heading>
        <Stack spacing="xl">
          <Input
            label={t('common.labels.phone')}
            error={formState.errors.phone?.message}
            {...register('phone', { required: t('home.contactForm.phoneRequired') })}
          />
          <Input
            label={t('common.labels.company')}
            error={formState.errors.company?.message}
            {...register('company', { required: t('home.contactForm.companyRequired') })}
          />
          <Stack spacing="xs">
            <TextArea
              label={t('common.labels.message')}
              placeholder={t('home.contactForm.messagePlaceholder')}
              maxLength={MAX_MESSAGE_LENGTH}
              error={formState.errors.message?.message}
              {...register('message', {
                required: t('home.contactForm.messageRequired'),
                maxLength: {
                  value: MAX_MESSAGE_LENGTH,
                  message: t('home.contactForm.messageMaxLength', { max: MAX_MESSAGE_LENGTH }),
                },
              })}
            />
            <span className="ap:self-end ap:text-xs ap:text-content-secondary">
              {messageLength} / {MAX_MESSAGE_LENGTH}
            </span>
          </Stack>
        </Stack>
        <Button variant="primary" onClick={submit} disabled={formState.isSubmitting}>
          {t('common.actions.send')}
        </Button>
        {status === 'success' && <StatusMessage variant="success" message={t('home.contactForm.success')} />}
        {status === 'error' && <StatusMessage variant="danger" message={t('home.contactForm.error')} />}
      </form>
    </Card>
  )
}

function getClaim(key: string): string {
  const value = getUserInfo()?.[key]
  return typeof value === 'string' ? value : ''
}

function getDefaultValues(): ContactRequest {
  return {
    phone: getClaim('phone_number'),
    company: '',
    message: '',
  }
}
